<template>
  <div class="address-picker">
    <button class="address-trigger" type="button" @click="openDialog">
      <span>{{ value || placeholder }}</span>
      <strong>选择</strong>
    </button>

    <div v-if="hasCoordinates" class="selected-summary">
      已选地址：{{ value || '已在地图上确认位置' }}
    </div>

    <div
      v-if="dialogVisible"
      class="address-modal-backdrop"
      @click.self="closeDialog"
    >
      <section class="address-modal-panel">
        <header class="address-modal-header">
          <div>
            <span>GPS/WGS84 LOCATION</span>
            <h3>{{ dialogTitle }}</h3>
          </div>
          <button type="button" @click="closeDialog">关闭</button>
        </header>

        <div class="picker-layout">
          <section class="picker-panel">
            <div class="search-row">
              <input
                v-model.trim="searchKeyword"
                :placeholder="searchPlaceholder"
                :disabled="mapLoading"
                @keydown.enter.stop.prevent
                @keyup.enter.stop.prevent="searchCandidates"
              />
              <button
                type="button"
                :disabled="!mapReady || mapLoading || searching"
                @click="searchCandidates"
              >
                {{ searching ? '搜索中...' : '搜索' }}
              </button>
            </div>

            <p class="panel-tip">{{ serviceTip }}</p>

            <div class="candidate-list">
              <button
                v-for="row in candidates"
                :key="`${row.lng}-${row.lat}-${row.name}`"
                type="button"
                :class="['candidate-row', { active: selectedCandidate && selectedCandidate.name === row.name && selectedCandidate.address === row.address }]"
                @click="previewCandidate(row)"
              >
                <strong>{{ row.name }}</strong>
                <span>{{ row.address }}</span>
              </button>
              <p v-if="!searching && candidates.length === 0" class="empty-candidate">暂无候选地点，请输入关键词搜索。</p>
            </div>
          </section>

          <section class="map-panel">
            <div class="map-stage">
              <iframe
                v-if="googleMapsAvailable !== false"
                class="map-canvas"
                :src="googleMapUrl"
                title="Google Maps location preview"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
              ></iframe>
              <div v-else ref="mapContainer" class="map-canvas"></div>
              <div v-if="googleMapsAvailable === null" class="map-loading-mask">正在连接 Google Maps...</div>
            </div>
            <div class="selection-card" v-if="selectedCandidate">
              <strong>{{ selectedCandidate.name }}</strong>
              <p>{{ selectedCandidate.address }}</p>
              <span>已确认此地址，系统将自动保存定位信息。</span>
            </div>
            <div class="selection-card empty" v-else>
              搜索并选择候选地点后，可在地图上查看预览。
            </div>
          </section>
        </div>

        <footer class="address-modal-actions">
          <button type="button" class="ghost" @click="closeDialog">取消</button>
          <button type="button" class="primary" :disabled="!selectedCandidate" @click="confirmSelection">确定</button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import { loadAmapPlugins } from '../utils/amap'
import { gcj02ToWgs84, wgs84ToGcj02 } from '../utils/coordinates'

const DEFAULT_CENTER = [121.4737, 31.2304]
const AMAP_PLUGINS = ['AMap.PlaceSearch', 'AMap.Geocoder', 'AMap.Scale', 'AMap.ToolBar']

export default {
  name: 'AddressPicker',
  props: {
    value: {
      type: String,
      default: ''
    },
    lat: {
      type: [Number, String],
      default: ''
    },
    lng: {
      type: [Number, String],
      default: ''
    },
    placeholder: {
      type: String,
      default: '请选择地址'
    },
    dialogTitle: {
      type: String,
      default: '选择位置'
    },
    searchPlaceholder: {
      type: String,
      default: '请输入地址关键词'
    }
  },
  data() {
    return {
      dialogVisible: false,
      searchKeyword: '',
      searching: false,
      candidates: [],
      selectedCandidate: null,
      amapSdk: null,
      map: null,
      marker: null,
      geocoder: null,
      placeSearch: null,
      googleMapsAvailable: null,
      mapReady: false,
      mapLoading: false,
      mapLoadError: ''
    }
  },
  computed: {
    hasCoordinates() {
      return this.lat !== '' && this.lng !== '' && this.lat !== null && this.lng !== null
    },
    serviceTip() {
      if (this.mapLoading) {
        return '地图服务正在加载，请稍候。'
      }
      if (this.mapLoadError) {
        return this.mapLoadError
      }
      if (this.googleMapsAvailable === false) {
        return '当前网络无法访问 Google Maps，已自动切换备用地图；保存的仍是通用 WGS84 坐标。'
      }
      if (!this.mapReady) {
        return '地图服务准备好后即可搜索地址。'
      }
      return '搜索地址后可对比多个候选地点，并在 Google 地图中确认最终位置。'
    },
    googleMapUrl() {
      const candidate = this.selectedCandidate
      const query = candidate && Number.isFinite(Number(candidate.lat)) && Number.isFinite(Number(candidate.lng))
        ? `${Number(candidate.lat)},${Number(candidate.lng)}`
        : `${DEFAULT_CENTER[1]},${DEFAULT_CENTER[0]}`
      return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${candidate ? 16 : 12}&output=embed`
    }
  },
  methods: {
    openDialog() {
      this.dialogVisible = true
      this.searchKeyword = this.value || this.searchKeyword
      this.$nextTick(() => this.handleDialogOpen())
    },
    closeDialog() {
      this.dialogVisible = false
      this.$nextTick(() => this.destroyFallbackMap())
    },
    async handleDialogOpen() {
      try {
        await this.ensureSearchServices()
        await this.detectGoogleMaps()
        if (this.googleMapsAvailable === false) {
          await this.$nextTick()
          this.ensureFallbackMap()
        }
        this.restoreCurrentSelection()
      } catch (error) {
        console.error('[AddressPicker] location search init failed:', error)
        this.mapLoadError = `地址搜索服务加载失败：${error.message || '请检查网络'}`
        this.$message.error(this.mapLoadError)
      }
    },
    async ensureSearchServices() {
      if (this.geocoder && this.placeSearch) {
        return
      }

      this.mapLoading = true
      this.mapReady = false
      this.mapLoadError = ''

      const AMap = await loadAmapPlugins(AMAP_PLUGINS)
      try {
        this.amapSdk = AMap
        this.geocoder = new AMap.Geocoder()
        this.placeSearch = new AMap.PlaceSearch({
          pageSize: 10,
          pageIndex: 1,
          city: '全国',
          citylimit: false
        })
        this.mapReady = true
      } finally {
        this.mapLoading = false
      }
    },
    async detectGoogleMaps() {
      if (this.googleMapsAvailable !== null) {
        return
      }
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), 3500)
      try {
        await window.fetch(`https://www.google.com/favicon.ico?map-probe=${Date.now()}`, {
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal
        })
        this.googleMapsAvailable = true
      } catch (error) {
        console.warn('[AddressPicker] Google Maps is unavailable, using fallback map:', error && error.message)
        this.googleMapsAvailable = false
      } finally {
        window.clearTimeout(timeout)
      }
    },
    ensureFallbackMap() {
      if (this.map || !this.amapSdk || !this.$refs.mapContainer) {
        return
      }
      const AMap = this.amapSdk
      this.map = new AMap.Map(this.$refs.mapContainer, {
        zoom: 13,
        center: DEFAULT_CENTER,
        viewMode: '2D',
        resizeEnable: true,
        mapStyle: 'amap://styles/normal',
        features: ['bg', 'road', 'building', 'point']
      })
      this.map.addControl(new AMap.Scale())
      this.map.addControl(new AMap.ToolBar())
    },
    destroyFallbackMap() {
      if (this.map && typeof this.map.destroy === 'function') {
        this.map.destroy()
      }
      this.map = null
      this.marker = null
    },
    restoreCurrentSelection() {
      if (this.hasCoordinates) {
        this.applyCandidate({
          name: this.value || '当前选择',
          address: this.value || '',
          lng: Number(this.lng),
          lat: Number(this.lat)
        })
        return
      }

      if (this.value) {
        this.searchCandidates()
        return
      }
      if (this.map) {
        this.map.setCenter(DEFAULT_CENTER)
        this.refreshFallbackMap()
      }
    },
    async searchCandidates() {
      if (!this.searchKeyword) {
        this.$message.warning('请输入地址关键词')
        return
      }

      if (!this.placeSearch) {
        try {
          await this.ensureSearchServices()
        } catch (error) {
          console.error('[AddressPicker] AMap search init failed:', error)
          this.mapLoadError = `地图服务初始化失败：${error.message || '请检查 Key 或网络'}`
          this.$message.error(this.mapLoadError)
          return
        }
      }

      if (!this.placeSearch) {
        this.$message.error('地图服务仍未就绪，请稍后重试')
        return
      }

      this.searching = true
      this.selectedCandidate = null
      this.placeSearch.search(this.searchKeyword, (status, result) => {
        console.info('[AddressPicker] PlaceSearch result:', status, result)
        const pois = status === 'complete' && result && result.poiList ? result.poiList.pois || [] : []
        const mapped = pois.map((item, index) => this.normalizeCandidate(item, index)).filter(Boolean)

        if (mapped.length > 0) {
          this.candidates = mapped
          this.$nextTick(() => this.applyCandidate(mapped[0]))
          this.searching = false
          return
        }

        this.searchByGeocoder(result)
      })
    },
    searchByGeocoder(placeSearchResult) {
      if (!this.geocoder) {
        this.searching = false
        return
      }

      this.geocoder.getLocation(this.searchKeyword, (status, result) => {
        console.info('[AddressPicker] Geocoder result:', status, result)
        const geocodes = status === 'complete' && result ? result.geocodes || [] : []
        const mapped = geocodes.map((item, index) => this.normalizeCandidate(item, index)).filter(Boolean)

        this.candidates = mapped
        this.searching = false

        if (mapped.length === 0) {
          const reason = this.formatAmapError(placeSearchResult || result)
          this.$message.warning(reason || '未找到匹配的位置')
          return
        }

        this.$nextTick(() => this.applyCandidate(mapped[0]))
      })
    },
    formatAmapError(result) {
      if (!result) {
        return ''
      }

      const message = result.info || result.message || result.status || ''
      if (!message) {
        return ''
      }

      return `地图搜索未返回结果：${message}`
    },
    normalizeCandidate(item, index) {
      const coords = this.extractCoords(item.location)
      if (!coords) {
        return null
      }

      const name = item.name || item.formattedAddress || `候选地点 ${index + 1}`
      const address = item.formattedAddress || [
        item.pname,
        item.cityname,
        item.adname,
        item.address,
        item.name
      ].filter(Boolean).join('')

      return {
        name,
        address: address || name,
        amapLng: coords.lng,
        amapLat: coords.lat,
        ...gcj02ToWgs84(coords.lng, coords.lat)
      }
    },
    extractCoords(location) {
      if (!location) {
        return null
      }

      if (typeof location.getLng === 'function' && typeof location.getLat === 'function') {
        return {
          lng: Number(location.getLng()),
          lat: Number(location.getLat())
        }
      }

      if (location.lng !== undefined && location.lat !== undefined) {
        return {
          lng: Number(location.lng),
          lat: Number(location.lat)
        }
      }

      return null
    },
    handleCurrentChange(row) {
      if (!row) {
        return
      }
      this.applyCandidate(row)
    },
    previewCandidate(row) {
      this.applyCandidate(row)
    },
    applyCandidate(candidate) {
      this.selectedCandidate = candidate
      if (!this.map || !this.amapSdk) {
        return
      }
      const point = candidate && candidate.amapLng !== undefined && candidate.amapLat !== undefined
        ? { lng: Number(candidate.amapLng), lat: Number(candidate.amapLat) }
        : wgs84ToGcj02(Number(candidate.lng), Number(candidate.lat))
      if (!this.marker) {
        this.marker = new this.amapSdk.Marker({
          position: [point.lng, point.lat],
          map: this.map
        })
      } else {
        this.marker.setPosition([point.lng, point.lat])
      }
      this.map.setCenter([point.lng, point.lat])
      this.map.setZoom(16)
      this.refreshFallbackMap()
    },
    refreshFallbackMap() {
      if (!this.map) return
      this.$nextTick(() => this.map && this.map.resize())
    },
    confirmSelection() {
      if (!this.selectedCandidate) {
        return
      }

      this.$emit('input', this.selectedCandidate.address)
      this.$emit('update:modelValue', this.selectedCandidate.address)
      this.$emit('change', {
        address: this.selectedCandidate.address,
        lat: this.selectedCandidate.lat,
        lng: this.selectedCandidate.lng,
        name: this.selectedCandidate.name
      })
      this.closeDialog()
    }
  }
}
</script>

<style scoped>
.address-trigger {
  width: 100%;
  min-width: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.28);
  color: #eef7ff;
  text-align: left;
  background: rgba(3, 10, 18, 0.86);
  cursor: pointer;
}

.address-trigger span {
  min-width: 0;
  overflow: hidden;
  color: rgba(238, 247, 255, 0.72);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.address-trigger strong {
  flex: 0 0 auto;
  padding: 7px 12px;
  border-radius: 8px;
  color: #041019;
  background: linear-gradient(135deg, #8ee7ff, #74f2bf);
}

.selected-summary {
  margin-top: 8px;
  color: rgba(238, 244, 255, 0.68);
  font-size: 12px;
  line-height: 1.6;
}

.address-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(1, 7, 13, 0.74);
  backdrop-filter: blur(10px);
}

.address-modal-panel {
  width: min(1040px, 100%);
  max-height: min(90vh, 860px);
  overflow: auto;
  padding: 22px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.2);
  color: #eef7ff;
  background:
    radial-gradient(circle at top left, rgba(142, 231, 255, 0.14), transparent 34%),
    linear-gradient(145deg, #08131f, #0c1827 54%, #11151d);
  box-shadow: 0 32px 96px rgba(0, 0, 0, 0.62);
}

.address-modal-header,
.address-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.address-modal-header {
  margin-bottom: 18px;
}

.address-modal-header span {
  color: #8ee7ff;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.address-modal-header h3 {
  margin: 8px 0 0;
  font-size: 26px;
}

.address-modal-header button,
.address-modal-actions button,
.search-row button {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.18);
  color: #eef7ff;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
}

.picker-layout {
  display: grid;
  grid-template-columns: minmax(320px, 0.95fr) minmax(0, 1.25fr);
  gap: 18px;
  align-items: start;
}

.search-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
}

.search-row input {
  min-height: 44px;
  padding: 0 13px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.2);
  color: #eef7ff;
  background: rgba(3, 10, 18, 0.76);
  outline: none;
}

.search-row input:focus {
  border-color: rgba(142, 231, 255, 0.46);
}

.panel-tip {
  margin: 10px 0 14px;
  color: rgba(238, 247, 255, 0.62);
  font-size: 13px;
  line-height: 1.6;
}

.candidate-list {
  display: grid;
  gap: 10px;
  max-height: 360px;
  overflow: auto;
}

.candidate-row {
  display: grid;
  gap: 6px;
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.14);
  color: #eef7ff;
  text-align: left;
  background: rgba(255, 255, 255, 0.045);
  cursor: pointer;
}

.candidate-row.active {
  border-color: rgba(116, 242, 191, 0.38);
  background: rgba(116, 242, 191, 0.08);
}

.candidate-row span,
.empty-candidate {
  color: rgba(238, 247, 255, 0.62);
  font-size: 12px;
  line-height: 1.5;
}

.map-panel {
  display: grid;
  gap: 12px;
}

.map-stage {
  position: relative;
  min-height: 420px;
}

.map-canvas {
  display: block;
  width: 100%;
  height: 420px;
  border: 0;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #f4f7fb, #e4ebf7);
}

.map-loading-mask {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: rgba(238, 247, 255, 0.72);
  background: rgba(8, 19, 31, 0.86);
  pointer-events: none;
}

.selection-card {
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid rgba(142, 231, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: #eef7ff;
}

.selection-card strong {
  display: block;
  margin-bottom: 8px;
  font-size: 15px;
}

.selection-card p {
  margin: 0 0 6px;
  line-height: 1.6;
}

.selection-card span {
  color: rgba(142, 231, 255, 0.8);
  font-size: 12px;
}

.selection-card.empty {
  color: rgba(238, 247, 255, 0.62);
}

.address-modal-actions {
  justify-content: flex-end;
  margin-top: 18px;
}

.address-modal-actions .primary {
  color: #041019;
  font-weight: 900;
  background: linear-gradient(135deg, #8ee7ff, #74f2bf);
}

.address-modal-actions button:disabled,
.search-row button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .picker-layout {
    grid-template-columns: 1fr;
  }

  .map-canvas {
    height: 320px;
  }

  .map-stage {
    min-height: 320px;
  }
}

@media (max-width: 600px) {
  .address-modal-backdrop {
    place-items: stretch;
    padding: 0;
  }

  .address-modal-panel {
    width: 100%;
    max-height: 100vh;
    min-height: 100vh;
    padding: 14px;
    border: 0;
    border-radius: 0;
  }

  .address-modal-header {
    position: sticky;
    top: -14px;
    z-index: 4;
    margin: -14px -14px 14px;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(142, 231, 255, 0.16);
    background: rgba(8, 19, 31, 0.96);
    backdrop-filter: blur(14px);
  }

  .address-modal-header h3 {
    margin-top: 4px;
    font-size: 19px;
  }

  .address-modal-header span {
    font-size: 8px;
  }

  .address-modal-header button,
  .address-modal-actions button,
  .search-row button {
    min-height: 38px;
    padding: 0 12px;
    font-size: 12px;
  }

  .picker-layout {
    gap: 12px;
  }

  .search-row {
    grid-template-columns: minmax(0, 1fr) 68px;
    gap: 7px;
  }

  .candidate-list {
    max-height: 210px;
  }

  .candidate-row {
    padding: 10px;
  }

  .map-canvas {
    height: 260px;
  }

  .map-stage {
    min-height: 260px;
  }

  .selection-card {
    padding: 11px 12px;
  }

  .address-modal-actions {
    position: sticky;
    bottom: -14px;
    z-index: 4;
    margin: 12px -14px -14px;
    padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(142, 231, 255, 0.16);
    background: rgba(8, 19, 31, 0.96);
    backdrop-filter: blur(14px);
  }

  .address-modal-actions button {
    flex: 1;
  }
}
</style>
